import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Check, Xmark, Mail, Lock } from 'iconoir-react';
import { ConfirmModal } from './ConfirmModal';
import {
  BoardJoinLimitErrorCode,
  getBoardJoinLimitContent,
  isBoardJoinLimitErrorCode,
} from '../utils/boardJoinLimitMessages';

interface Invitation {
  id: number;
  board_id: number;
  board_name: string;
  board_color: string | null;
  role: string;
  invited_by: string;
  created_at: string;
}

interface BoardLimitBlock {
  code: BoardJoinLimitErrorCode;
  redirect: string;
}

interface AcceptedBoard {
  id: number;
}

export const PendingInvitations: React.FC = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [invitationToDecline, setInvitationToDecline] = useState<number | null>(null);
  const [boardLimitBlock, setBoardLimitBlock] = useState<BoardLimitBlock | null>(null);
  const [acceptedBoard, setAcceptedBoard] = useState<AcceptedBoard | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      const response = await api.get('/invitations/pending');
      setInvitations(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitationId: number) => {
    try {
      const response = await api.post(`/invitations/${invitationId}/accept`);

      setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));

      if (response.data.board) {
        setAcceptedBoard({ id: response.data.board.id });
      }
    } catch (error: any) {
      const errorCode = error.response?.data?.error;
      if (error.response?.status === 403 && isBoardJoinLimitErrorCode(errorCode)) {
        setBoardLimitBlock({
          code: errorCode,
          redirect: error.response.data.redirect || '/account?tab=subscription',
        });
        return;
      }
      alert(error.response?.data?.message || error.response?.data?.error || 'Erreur lors de l\'acceptation de l\'invitation');
    }
  };

  const handleGoToBoardLimitRedirect = () => {
    if (!boardLimitBlock) return;
    const redirect = boardLimitBlock.redirect;
    setBoardLimitBlock(null);
    navigate(redirect);
  };

  const handleOpenAcceptedBoard = () => {
    if (!acceptedBoard) return;
    const boardId = acceptedBoard.id;
    setAcceptedBoard(null);
    navigate(`/boards/${boardId}`);
  };

  const handleDecline = (invitationId: number) => {
    setInvitationToDecline(invitationId);
  };

  const handleConfirmDecline = async () => {
    const invitationId = invitationToDecline;
    setInvitationToDecline(null);
    if (invitationId === null) return;

    try {
      await api.post(`/invitations/${invitationId}/decline`);
      await loadInvitations();
    } catch (error) {
      alert('Erreur lors du refus de l\'invitation');
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: { [key: string]: string } = {
      admin: 'Administrateur',
      editor: 'Éditeur',
      visitor: 'Visiteur',
    };
    return labels[role] || role;
  };

  const getRoleColor = (role: string) => {
    const colors: { [key: string]: string } = {
      admin: '#e74c3c',
      editor: '#3498db',
      visitor: '#95a5a6',
    };
    return colors[role] || '#95a5a6';
  };

  if (loading) {
    return null;
  }

  if (invitations.length === 0 && !acceptedBoard && !boardLimitBlock) {
    return null;
  }

  const boardLimitContent = boardLimitBlock ? getBoardJoinLimitContent(boardLimitBlock.code) : null;

  return (
    <div className="pending-invitations">
      {invitations.length > 0 && (
        <>
          <div className="invitations-header">
            <Mail width={24} height={24} strokeWidth={2} />
            <h3>Invitations en attente ({invitations.length})</h3>
          </div>

          <div className="invitations-list">
            {invitations.map((invitation) => (
              <div key={invitation.id} className="invitation-card">
                <div className="invitation-info">
                  <div
                    className="invitation-board-badge"
                    style={{
                      backgroundColor: invitation.board_color || '#3498db',
                      color: 'white'
                    }}
                  >
                    {invitation.board_name}
                  </div>
                  <div className="invitation-details">
                    <p className="invitation-text">
                      <strong>{invitation.invited_by}</strong> vous invite à rejoindre ce tableau en tant que{' '}
                      <span
                        className="invitation-role"
                        style={{ color: getRoleColor(invitation.role) }}
                      >
                        {getRoleLabel(invitation.role)}
                      </span>
                    </p>
                    <p className="invitation-date">
                      {new Date(invitation.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                </div>

                <div className="invitation-actions">
                  <button
                    className="btn btn-accept"
                    onClick={() => handleAccept(invitation.id)}
                    title="Accepter l'invitation"
                  >
                    <Check width={18} height={18} strokeWidth={2} />
                    Accepter
                  </button>
                  <button
                    className="btn btn-decline"
                    onClick={() => handleDecline(invitation.id)}
                    title="Refuser l'invitation"
                  >
                    <Xmark width={18} height={18} strokeWidth={2} />
                    Refuser
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <ConfirmModal
        isOpen={invitationToDecline !== null}
        title="Refuser l'invitation"
        message="Êtes-vous sûr de vouloir refuser cette invitation ?"
        confirmText="Refuser"
        onConfirm={handleConfirmDecline}
        onCancel={() => setInvitationToDecline(null)}
        variant="danger"
      />

      {boardLimitBlock && boardLimitContent && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>
              <Lock width={18} height={18} strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              {boardLimitContent.title}
            </h2>

            <p>{boardLimitContent.message}</p>
            <p>{boardLimitContent.secondary}</p>

            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleGoToBoardLimitRedirect}>
                {boardLimitContent.ctaLabel}
              </button>
              <button className="btn btn-secondary" onClick={() => setBoardLimitBlock(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {acceptedBoard && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>
              <Check width={18} height={18} strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Invitation acceptée !
            </h2>

            <p>Vous avez rejoint le tableau avec succès.</p>

            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleOpenAcceptedBoard}>
                Ouvrir le tableau
              </button>
              <button className="btn btn-secondary" onClick={() => setAcceptedBoard(null)}>
                Retour au dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
